import { useSearchParams } from 'react-router-dom'

import {
  Box,
  Button,
  Card,
  EnhancedDimension,
  Tag,
  Text,
} from '@factorialco/deprecated-design-system'
import { PerformanceReviewOverviewPendingAgreementsTaskFragment } from 'generated/resources/graphql'

import Sidepanel from 'modules/core/components/Sidepanel'
import i18n from 'modules/core/lib/i18n'

import { MyAgreements } from 'modules/performance_process/components/overview/myAgreements'

interface Props {
  reviewProcess: PerformanceReviewOverviewPendingAgreementsTaskFragment
  minWidth?: EnhancedDimension
}

export const PendingAgreementsTask = ({ reviewProcess, minWidth }: Props) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const isSidepanelOpen = searchParams.get('myAgreementsPanel') === 'open'

  const directReportsCount = reviewProcess.directReportsTargets.totalCount ?? 0
  const isVisible =
    reviewProcess.agreementsConfiguration.enabled &&
    reviewProcess.reviewerStrategies?.includes('manager') &&
    directReportsCount > 0

  if (!isVisible) return null

  const targetsPendingInitiateAgreement =
    reviewProcess.directReportsTargetsPendingInitiateAgreement.totalCount ?? 0
  const targetsWithSignedAgreement =
    reviewProcess.directReportsTargetsWithSignedAgreement.totalCount ?? 0
  const targetsWithPendingAgreement =
    reviewProcess.directReportsTargetsWithPendingAgreement.totalCount ?? 0

  const isDisabled =
    targetsPendingInitiateAgreement + targetsWithSignedAgreement + targetsWithPendingAgreement === 0
  const isPending = targetsPendingInitiateAgreement + targetsWithPendingAgreement > 0
  const isCompleted = targetsWithSignedAgreement === directReportsCount

  const onClick = () => setSearchParams({ myAgreementsPanel: 'open' })

  return (
    <Box minWidth={minWidth} width={minWidth}>
      <Card onClick={isDisabled ? undefined : onClick}>
        <Card.Title
          size="small"
          action={
            <>
              {!isCompleted && isPending && (
                <Tag leftItem="circle" inline capitalize type="tangerine">
                  {i18n.tp('performance_process.overview.tasks.agreements.pending', {
                    count: targetsPendingInitiateAgreement + targetsWithPendingAgreement,
                  })}
                </Tag>
              )}
              {!isCompleted && isDisabled && (
                <Tag leftItem="circle" inline capitalize type="grey">
                  {i18n.t('performance_process.overview.tasks.agreements.not_available_yet')}
                </Tag>
              )}
            </>
          }
        >
          {i18n.t('performance_process.overview.tasks.agreements.title')}
        </Card.Title>
        <Card.Content>
          <Box gap="s16" width="full" flexGrow justifyContent="spaceBetween">
            <Text.Normal color="grey1000">
              {i18n.t('performance_process.overview.tasks.agreements.description')}
            </Text.Normal>
            {!isDisabled && (
              <Button onClick={onClick} type="secondary">
                {isCompleted
                  ? i18n.t('performance_process.overview.tasks.view_all')
                  : i18n.t('performance_process.overview.tasks.agreements.action')}
              </Button>
            )}
          </Box>
        </Card.Content>
      </Card>

      <Sidepanel
        open={isSidepanelOpen}
        onClose={() => setSearchParams({})}
        width="wideXL"
        title={i18n.t('performance_process.agreements.my_agreements.title_new')}
      >
        <MyAgreements
          canInitiateAgreemensInBulk={reviewProcess._policies?.bulkInititateAgreement.can ?? false}
          republishEvaluationsEnabled={
            reviewProcess.republishEvaluationsConfiguration?.enabled ?? false
          }
        />
      </Sidepanel>
    </Box>
  )
}
